import axios from "axios";

// Pick the backend URL at runtime. If the configured REACT_APP_BACKEND_URL
// points to a *different* host than the page itself, we fall back to the
// page origin so the browser makes same-origin requests — this dodges the
// CORS-with-credentials trap on production where the SPA is served from
// `careforflora.com` but the env var still points at the internal
// `greencare-stack.emergent.host` backend hostname. In that setup
// `careforflora.com/api/*` is already proxied to the backend by Emergent's
// ingress, so a relative URL just works.
const CONFIGURED = process.env.REACT_APP_BACKEND_URL || "";

const pickBackendUrl = () => {
  if (typeof window === "undefined") return CONFIGURED;
  if (!CONFIGURED) return ""; // empty → use relative URLs
  try {
    const configuredHost = new URL(CONFIGURED).host;
    const pageHost = window.location.host;
    if (configuredHost && pageHost && configuredHost !== pageHost) {
      // Different host than the page → use same-origin to avoid CORS.
      return "";
    }
  } catch (_) { /* malformed URL — fall through */ }
  return CONFIGURED;
};

const BACKEND_URL = pickBackendUrl();
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "cf_token";

export const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const compressImageToWebP = (file, maxSize = 1024, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) {
          height = (maxSize / width) * height;
          width = maxSize;
        } else if (height > maxSize) {
          width = (maxSize / height) * width;
          height = maxSize;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/webp", quality);
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
